import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {OptimizationNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {OptimizationCardScratchpadState} from '../../scratchpad/optimizationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const setOutputsCountInputSchema = z.object({
  count: z
    .number()
    .int({message: 'Count must be a whole number'})
    .min(2, {message: 'Optimization cards must have at least 2 outputs'})
    .describe('The number of outputs (minimum 2)'),
});

export default function setOutputsCountTool() {
  async function execute(
    params: ToolExecuteParams<typeof setOutputsCountInputSchema, OptimizationCardScratchpadState>,
  ) {
    const {count} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<OptimizationCardScratchpadState>(
      agentScratchpad,
      NodeType.GENERAL_OPTIMIZATION,
    );

    const currentCount = validatedScratchpad.card.outputs.length;

    if (count === currentCount) {
      return toolResult(`Output count is already ${count}`);
    }

    if (count > currentCount) {
      // Add outputs
      const outputsToAdd = count - currentCount;
      const nextId =
        currentCount > 0 ? Math.max(...validatedScratchpad.card.outputs.map((o) => o.id), 0) + 1 : 1;

      for (let i = 0; i < outputsToAdd; i++) {
        const newOutput: OptimizationNodeOutputData = {
          id: nextId + i,
          nodeId: NO_NODE_ID,
          name: `Output ${nextId + i}`,
          type: OutputType.Regular,
        };
        validatedScratchpad.card.outputs.push(newOutput);
      }

      validatedScratchpad.card.variationsCount = count;

      return toolResult(`Added ${outputsToAdd} output(s). Total outputs: ${count}`);
    } else {
      // Remove outputs from the end
      const outputsToRemove = currentCount - count;
      validatedScratchpad.card.outputs = validatedScratchpad.card.outputs.slice(
        0,
        count,
      ) as OptimizationNodeOutputData[];
      validatedScratchpad.card.variationsCount = count;

      return toolResult(`Removed ${outputsToRemove} output(s). Total outputs: ${count}`);
    }
  }

  return tool<typeof setOutputsCountInputSchema, OptimizationCardScratchpadState>({
    name: V2CardAgentToolName.SetOutputsCount,
    displayName: 'Setting outputs count',
    description: 'Set the number of outputs. Outputs are added/removed automatically (minimum 2).',
    inputSchema: setOutputsCountInputSchema,
    execute,
  });
}
